import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useOrganization } from "../../context/organization/organization";
import classNames from "./CreateOrEditRoom.module.css";
import api, {
  CreateRoomRequest,
  CreateRoomResponse,
  Organization,
  Room,
  UpdateRoomRequest,
  UpdateRoomResponse,
} from "../../../hooks/api-new";
import { useAxios } from "../../../hooks/axios";
import Loader from "../../common/Loader";
import * as Form from "@radix-ui/react-form";
import LanguageMultiSelect from "../../common/LanguageMultiSelect";
import VoiceOptionsDialog from "../../common/dialogs/voice/VoiceOptionsDialog";
import { useRoom, useRoomId } from "../../context/room/room";
import { toast } from "react-toastify";
import { fromZonedTime } from "date-fns-tz";
import { AxiosError, AxiosResponse } from "axios";
import eventImg from "../../../assets/form/event.png";
import meetingImg from "../../../assets/stock/meeting.webp";
import Button from "../../common/Button";
import Toggle from "../../common/Toggle";
import InvalidMessage from "../../common/InvalidMessage";
import Select, { SelectItem } from "../../common/Select";
import {
  IoCheckmark,
  IoChevronDown,
  IoCopyOutline,
  IoLink,
} from "react-icons/io5";
import Breadcrumbs from "../../common/Breadcrumbs";
import UpgradeNow from "../../common/UpgradeNow";
import teamsImg from "../../../assets/stock/integration.webp";
import RadixDialog from "../../common/dialogs/RadixDialog";
import { getUrl } from "../../../types";
import { timezones } from "./timezones";

const JoinMeetingDialog = (props: {
  open?: boolean;
  onOpenChange: (open: boolean) => void;
  link: string;
}) => {
  const navigate = useNavigate();
  const [showCopied, setShowCopied] = useState(false);

  return (
    <RadixDialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={"Schedule or join Langfinity meetings"}
      content={
        <div className={classNames.JoinMeetingDialogContent}>
          <div className={classNames.FormField}>
            <label>Schedule a meeting with this room</label>
            <div className={classNames.JoinMeetingLink}>
              <IoLink />
              {props.link}
            </div>
            <Button
              color="gray"
              backgroundColor="black"
              borderColor="dark-gray"
              onClick={() => {
                navigator.clipboard.writeText(props.link);
                setShowCopied(true);
                setTimeout(() => {
                  setShowCopied(false);
                }, 2 * 1000);
              }}
            >
              {!showCopied ? (
                <>
                  <IoCopyOutline />
                  Copy link
                </>
              ) : (
                <>
                  <IoCheckmark />
                  Copied
                </>
              )}
            </Button>
          </div>

          <Button
            onClick={() => {
              const url = new URL(props.link);
              navigate(url.pathname);
            }}
          >
            Join now
          </Button>
        </div>
      }
    />
  );
};

const durations: number[] = [
  ...Array.from({ length: 16 }, (_, i) => (i + 1) * 30),
  1440,
];

const speakers: number[] = Array.from({ length: 10 }, (_, i) => i + 1);

type RoomFormProps = {
  organization: Organization;
  initialRoom: CreateRoomRequest | UpdateRoomRequest;
  hideSubmit?: boolean;
  edit?: boolean;
  onSubmit: (room: CreateRoomRequest | UpdateRoomRequest) => Promise<void>;
  onInvalid?: () => void;
  // TODO: Should probably use something cleaner here?
  triggerSubmit?: number;
  id?: string;
};

function RoomForm({
  organization,
  initialRoom,
  hideSubmit,
  edit,
  onSubmit,
  onInvalid,
  triggerSubmit,
  id,
}: RoomFormProps) {
  const [room, setRoom] = useState<CreateRoomRequest | UpdateRoomRequest>(
    initialRoom,
  );

  const getTimeZoneAbbreviation = useCallback((timeZone: string) => {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(new Date());
    const timeZonePart = parts.find((part) => part.type === "timeZoneName");
    return timeZonePart?.value;
  }, []);

  const defaultTimezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    Intl.DateTimeFormat().resolvedOptions().timeZoneName;
  const [selectedTimezone, setSelectedTimezone] = useState(
    defaultTimezone &&
      timezones.some((timezone) => timezone.value === defaultTimezone)
      ? defaultTimezone
      : "America/Los_Angeles",
  );

  const [durationInMinutes, setDurationInMutes] = useState(
    initialRoom.start_time && initialRoom.end_time
      ? (new Date(initialRoom.end_time).getTime() -
          new Date(initialRoom.start_time).getTime()) /
          (1000 * 60)
      : 30,
  );

  const available247 = useMemo(
    () => durationInMinutes >= 1440,
    [durationInMinutes],
  );

  function toLocalISOString(date: Date) {
    const pad = (num: number) => String(num).padStart(2, "0");

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1); // Months are 0-based
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  const [localStartTime, setLocalStartTime] = useState(
    initialRoom.start_time
      ? toLocalISOString(new Date(initialRoom.start_time))
      : toLocalISOString(new Date()),
  );

  useEffect(() => {
    const startTime = fromZonedTime(new Date(localStartTime), selectedTimezone);
    const endTime = new Date(startTime);
    endTime.setMinutes(endTime.getMinutes() + durationInMinutes);
    setRoom((prevState) => {
      if (prevState.room_type !== "event") {
        return prevState;
      }
      return {
        ...prevState,
        start_time: startTime,
        end_time: endTime,
        available_24_7: available247,
      };
    });
  }, [
    setRoom,
    localStartTime,
    selectedTimezone,
    durationInMinutes,
    available247,
  ]);

  const [clickedSubmit, setClickedSubmit] = useState(false);
  const validationMessages = useMemo(() => {
    const messages = [];
    if (room.room_type === "event" && !room.name?.length) {
      messages.push("event_name");
    }
    if (room.languages && room.languages.length < 2) {
      messages.push("at_least_2_languages");
    }
    if (organization.translation_seconds_remaining <= 0) {
      messages.push("no_minutes");
    }
    return messages;
  }, [room, organization]);

  const submit = useCallback(() => {
    setClickedSubmit(true);
    if (validationMessages.length) {
      onInvalid && onInvalid();
      return;
    }
    onSubmit(room);
  }, [onSubmit, onInvalid, room, validationMessages]);

  useEffect(() => {
    if (!triggerSubmit || triggerSubmit <= 0) {
      return;
    }
    submit();
  }, [triggerSubmit, submit]);

  const title = useMemo(() => {
    switch (room.room_type) {
      case "event": {
        if (edit) {
          return "Edit event";
        }
        return "Schedule translation for events and conferences";
      }
      case "meeting": {
        if (edit) {
          return "Edit meeting";
        }
        return "Schedule or join Langfinity meetings";
      }
      case "teams": {
        return "Languages and setting configuration";
      }
      default:
        return;
    }
  }, [edit, room.room_type]);

  const [showMoreOptions, setShowMoreOptions] = useState(false);

  const navigate = useNavigate();

  const formImg = useMemo(() => {
    switch (room.room_type) {
      case "event":
        return eventImg;
      case "meeting":
        return meetingImg;
      case "teams": {
        if (edit) {
          return teamsImg;
        }
      }
    }
  }, [room.room_type, edit]);

  const [joinMode, setJoinMode] = useState(false);
  const [meetingLink, setMeetingLink] = useState("");

  const buttonText = useMemo(() => {
    switch (room.room_type) {
      case "event":
        return "Schedule translation";
      case "meeting": {
        if (joinMode) {
          return "Join now";
        }
        return "Schedule now";
      }
    }
  }, [room.room_type, joinMode]);

  const roomTypeClassName = useMemo(() => {
    switch (room.room_type) {
      case "meeting":
        return classNames.MeetingRoom;
      case "teams":
        return classNames.TeamsRoom;
    }
    return "";
  }, [room.room_type]);

  const selectableTimezones: SelectItem[] = useMemo(() => {
    return timezones.map((timezone) => {
      const abbreviation = getTimeZoneAbbreviation(timezone.value);
      return {
        value: timezone.value,
        label: abbreviation
          ? `${timezone.name} (${abbreviation})`
          : timezone.name,
      };
    });
  }, [getTimeZoneAbbreviation]);

  return (
    <div className={`${classNames.CreateOrEditRoom} ${roomTypeClassName}`}>
      {room.room_type !== "teams" && (
        <Breadcrumbs
          items={[
            { label: "Home", url: "/" },
            { label: "Meetings", url: "/meetings" },
            ...(edit && room.name && id
              ? [
                  {
                    label: room.name,
                    url: `/${room.room_type}/${id}${
                      room.room_type === "event" ? "/details" : ""
                    }`,
                  },
                ]
              : []),
          ]}
        />
      )}

      <div className={classNames.FormAndImg}>
        <Form.Root
          className={classNames.Form}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          {title && <h1 className={classNames.Title}>{title}</h1>}

          {room.room_type === "meeting" && (
            <div className={classNames.CreateOrJoinMeeting}>
              <button
                type="button"
                aria-selected={!joinMode}
                onClick={() => setJoinMode(false)}
              >
                Schedule a meeting
              </button>
              <button
                type="button"
                aria-selected={joinMode}
                onClick={() => setJoinMode(true)}
              >
                Join a meeting
              </button>
            </div>
          )}

          {room.room_type === "event" && (
            <>
              <Form.Field name="name" className={classNames.FormField}>
                <Form.Label>Event name</Form.Label>
                <input
                  value={room.name || ""}
                  onChange={(e) =>
                    setRoom((prevState) => ({
                      ...prevState,
                      name: e.target.value,
                    }))
                  }
                  placeholder={
                    !edit ? "Ex. Language & leadership workshop" : "Event name"
                  }
                />
                {clickedSubmit && validationMessages.includes("event_name") && (
                  <InvalidMessage message="Please enter the event name to continue" />
                )}
              </Form.Field>
              <div className={classNames.EventTimeFields}>
                <Form.Field name="start_time" className={classNames.FormField}>
                  <Form.Label>Date and time</Form.Label>
                  <input
                    className={classNames.StartDate}
                    type="datetime-local"
                    value={localStartTime}
                    onChange={(e) => {
                      setLocalStartTime(e.target.value);
                    }}
                  />
                </Form.Field>
                <Form.Field name="timezone" className={classNames.FormField}>
                  <Form.Label>Timezone</Form.Label>
                  <Select
                    selected={selectedTimezone}
                    items={selectableTimezones}
                    onChange={(value) =>
                      typeof value === "string" && setSelectedTimezone(value)
                    }
                  />
                </Form.Field>
                <Form.Field name="duration" className={classNames.FormField}>
                  <Form.Label>Duration</Form.Label>
                  <Select
                    selected={durationInMinutes}
                    items={durations.map((duration) => ({
                      value: duration,
                      label: `${duration} min`,
                    }))}
                    onChange={(value) =>
                      typeof value === "number" && setDurationInMutes(value)
                    }
                  />
                </Form.Field>
              </div>
            </>
          )}

          {!joinMode ? (
            <>
              <Form.Field name="languages" className={classNames.FormField}>
                <Form.Label>Languages</Form.Label>
                <LanguageMultiSelect
                  selectedLanguages={room.languages || []}
                  maxLanguages={organization.max_languages_allowed}
                  onChange={(languages) =>
                    setRoom((prevState) => ({
                      ...prevState,
                      languages: languages,
                    }))
                  }
                />
                {clickedSubmit &&
                  validationMessages.includes("at_least_2_languages") && (
                    <InvalidMessage message="Please select at least two languages to continue" />
                  )}
              </Form.Field>

              <div>
                <div
                  className={classNames.MoreOptionsToggle}
                  onClick={() => setShowMoreOptions((prevState) => !prevState)}
                  aria-selected={showMoreOptions}
                >
                  {!showMoreOptions ? "More options" : "Fewer options"}
                </div>
                {showMoreOptions && (
                  <div className={classNames.MoreOptions}>
                    <div className={classNames.VoiceOptions}>
                      {organization.voice_translation_enabled && (
                        <Form.Field
                          name="default_voice_on"
                          className={classNames.FormField}
                        >
                          <Form.Label>Default to voice translation</Form.Label>
                          <Toggle
                            enabled={!!room.default_voice_on}
                            onChange={() =>
                              setRoom((prevState) => ({
                                ...prevState,
                                default_voice_on: !room.default_voice_on,
                              }))
                            }
                          />
                        </Form.Field>
                      )}
                      {organization.voice_translation_enabled && (
                        <Form.Field
                          name="default_voice_avatar"
                          className={classNames.FormField}
                        >
                          <Form.Label>Choose your voice avatar</Form.Label>
                          <VoiceOptionsDialog
                            locale={"en-US"}
                            trigger={
                              <Button
                                color="white"
                                backgroundColor="black"
                                borderColor="primary"
                              >
                                Voice avatar
                                <IoChevronDown />
                              </Button>
                            }
                            selectedVoiceId={room.default_voice_avatar}
                            onConfirm={(voiceId) =>
                              setRoom((prevState) => ({
                                ...prevState,
                                default_voice_avatar: voiceId,
                              }))
                            }
                            basic={!organization.voice_avatar_enabled}
                          />
                        </Form.Field>
                      )}
                    </div>
                    {organization.recording_enabled && (
                      <Form.Field
                        name="recording"
                        className={classNames.FormField}
                      >
                        <Form.Label>
                          Enable recording and meeting notes
                        </Form.Label>
                        <Toggle
                          enabled={!!room.auto_record}
                          onChange={() =>
                            setRoom((prevState) => ({
                              ...prevState,
                              auto_record: !room.auto_record,
                            }))
                          }
                        />
                      </Form.Field>
                    )}
                    {room.room_type === "event" && (
                      <>
                        <Form.Field name="qna" className={classNames.FormField}>
                          <Form.Label>Enable Q&A</Form.Label>
                          <Toggle
                            enabled={!!room.qna}
                            onChange={() =>
                              setRoom((prevState) => ({
                                ...prevState,
                                qna: !prevState.qna,
                              }))
                            }
                          />
                        </Form.Field>
                        <Form.Field
                          name="reoccurring"
                          className={classNames.FormField}
                        >
                          <Form.Label>Make re-occurring</Form.Label>
                          <Toggle
                            enabled={!!room.reoccurring}
                            onChange={() =>
                              setRoom((prevState) => ({
                                ...prevState,
                                reoccurring: !prevState.reoccurring,
                              }))
                            }
                          />
                        </Form.Field>
                        <Form.Field
                          name="num_speakers"
                          className={`${classNames.FormField} ${classNames.NumSpeakers}`}
                        >
                          <Form.Label>Number of speakers</Form.Label>
                          <Select
                            selected={room.num_speakers || 1}
                            items={speakers.map((num) => ({
                              value: num,
                              label: num.toString(),
                            }))}
                            onChange={(value) =>
                              typeof value === "number" &&
                              setRoom((prevState) => ({
                                ...prevState,
                                num_speakers: value,
                              }))
                            }
                          />
                        </Form.Field>
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <Form.Field name="meeting_link" className={classNames.FormField}>
              <label>Meeting link</label>
              <input
                type="text"
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                placeholder="Enter link"
              />
            </Form.Field>
          )}
          {!hideSubmit && (
            <div className={classNames.FormActions}>
              <Form.Submit asChild>
                <Button
                  onClick={
                    joinMode
                      ? () => {
                          const url = new URL(meetingLink);
                          navigate(url.pathname);
                        }
                      : undefined
                  }
                  type={joinMode ? "button" : "submit"}
                >
                  {!edit ? buttonText : "Save changes"}
                </Button>
              </Form.Submit>
              {room.room_type !== "teams" && (
                <Link
                  to="/"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(-1);
                  }}
                >
                  <Button
                    color="gray"
                    backgroundColor="black"
                    borderColor="dark-gray"
                  >
                    {!edit ? "Return to home" : "Cancel"}
                  </Button>
                </Link>
              )}
            </div>
          )}
          {clickedSubmit && validationMessages.includes("no_minutes") ? (
            <div className={classNames.InvalidMinutes}>
              <UpgradeNow
                organization={organization}
                goToCalendly={room.room_type === "teams"}
              />
              <InvalidMessage message="No minutes remaining" />
            </div>
          ) : (
            <UpgradeNow
              organization={organization}
              goToCalendly={room.room_type === "teams"}
            />
          )}
        </Form.Root>
        {formImg && <img src={formImg} alt={title} />}
      </div>
    </div>
  );
}

export type CreateOrEditRoomProps = {
  roomType?: Room["room_type"];
  hideSubmit?: boolean;
  teamsParams?: { deep_link?: string; teams_chat_id?: string };

  // This is a hack for teams since the "Save" button controls the form instead of the built-in submit
  triggerSubmit?: number;
  onCreatedOrUpdated?: (
    response:
      | AxiosResponse<CreateRoomResponse>
      | AxiosResponse<UpdateRoomResponse>,
  ) => void;
  onInvalid?: () => void;
  onError?: (error: AxiosError) => void;
};

export function CreateRoom(props: CreateOrEditRoomProps) {
  const navigate = useNavigate();
  const { organization, loading } = useOrganization();

  const params = useParams();
  const [createRoomRequest, setCreateRoomRequest] =
    useState<CreateRoomRequest>();

  useEffect(() => {
    if (!organization) {
      return;
    }
    setCreateRoomRequest({
      room_type:
        props.roomType || (params.roomType as CreateRoomRequest["room_type"]),
      languages: organization.default_languages_setting || [],
      default_voice_on: organization.default_ai_voice_setting,
      auto_record: organization.default_recording_setting,
      default_voice_avatar: organization.default_avatar_setting,
      qna: organization.default_qna_setting,
      ...props.teamsParams,
    });
  }, [props.roomType, props.teamsParams, params.roomType, organization]);

  const axios = useAxios();
  const createRoom = api.useCreateRoom(axios);

  const [meetingLink, setMeetingLink] = useState<string>();
  const [joinMeetingDialogOpen, setJoinMeetingDialogOpen] = useState(false);

  if (!organization || loading || !createRoomRequest) {
    return <Loader style={{ alignSelf: "center" }} />;
  }

  return (
    <>
      <RoomForm
        organization={organization}
        initialRoom={createRoomRequest}
        hideSubmit={props.hideSubmit}
        triggerSubmit={props.triggerSubmit}
        onSubmit={async (room) => {
          try {
            const response = await createRoom(room as CreateRoomRequest);

            // Navigate to event details
            if (response.data.room.room_type === "event") {
              navigate(
                `/${response.data.room.room_type}/${response.data.room.id}/details`,
              );
            }

            // Open join meeting dialog
            if (response.data.room.room_type === "meeting") {
              setMeetingLink(getUrl(`/meeting/${response.data.room.id}`));
              setJoinMeetingDialogOpen(true);
            }

            if (props.onCreatedOrUpdated) {
              props.onCreatedOrUpdated(response);
            }
          } catch (error) {
            if (error instanceof AxiosError && props.onError) {
              props.onError(error);
            }
          }
        }}
        onInvalid={props.onInvalid}
      />
      {meetingLink && (
        <JoinMeetingDialog
          link={meetingLink}
          open={joinMeetingDialogOpen}
          onOpenChange={setJoinMeetingDialogOpen}
        />
      )}
    </>
  );
}

export function EditRoom(props: CreateOrEditRoomProps) {
  const { organization, loading: orgLoading } = useOrganization();
  const { room, loading: roomLoading } = useRoom();

  const [updateRoomRequest, setUpdateRoomRequest] =
    useState<UpdateRoomRequest>();

  useEffect(() => {
    // Init update room request with room provider
    if (!room) {
      return;
    }
    setUpdateRoomRequest(room);
  }, [room]);

  const axios = useAxios();
  const updateRoom = api.useUpdateRoom(axios);

  if (
    !organization ||
    orgLoading ||
    !room ||
    roomLoading ||
    !updateRoomRequest
  ) {
    return <Loader />;
  }

  return (
    <RoomForm
      id={updateRoomRequest.id}
      organization={organization}
      initialRoom={updateRoomRequest}
      hideSubmit={props.hideSubmit}
      onInvalid={props.onInvalid}
      edit={true}
      onSubmit={async (room) => {
        try {
          const response = await updateRoom(room as UpdateRoomRequest);
          toast(
            <div className="Toast SuccessToast">
              <div>{"Successfully saved changes"}</div>
            </div>,
            { type: "success" },
          );
          if (props.onCreatedOrUpdated) {
            props.onCreatedOrUpdated(response);
          }
        } catch (error) {
          if (error instanceof AxiosError && props.onError) {
            props.onError(error);
          }
        }
      }}
    />
  );
}

export default function CreateOrEditRoom(props: CreateOrEditRoomProps) {
  const roomId = useRoomId();

  if (!roomId) {
    return <CreateRoom {...props} />;
  }

  return <EditRoom {...props} />;
}
